import {
  ChatListItem,
  cloneChat as apiCloneChat,
  deleteChat as apiDeleteChat,
  renameChat as apiRenameChat,
  toggleStarChat as apiToggleStarChat,
  listChats,
} from '@lib/network/chats';
import { userStore } from '@stores/User';
import {
  IObservableArray,
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

/**
 * ChatListStore — mirrors vibedit's `ChatListStore` for the StarZero
 * navbar's "Recent Chats" section. Data lives on the StarZero Agents
 * backend (see `@lib/network/chats`) and is read-only-ish from this
 * app's perspective: we can list, rename, star, clone, and delete,
 * but actually entering a chat happens in the vibedit web app.
 */
export class ChatListStore {
  @observable
  chats: ChatListItem[] = [];

  @observable
  fetching = false;

  @observable
  initialized = false;

  @observable
  searchQuery = '';

  private started = false;
  private disposeReaction: (() => void) | null = null;

  constructor() {
    makeObservable(this);
  }

  /** Wire up reactions and listeners. Must be called from a React
   *  effect (see `RecentChatsSection`) rather than at module load —
   *  the singleton is constructed during import, and reading
   *  `userStore.token` synchronously there hit a TDZ from a circular
   *  import. By the time a component mounts, all modules are
   *  evaluated, so it's safe.
   *
   *  Idempotent: re-calls while already started are no-ops. Returns
   *  the matching `stop()` function for the caller's effect cleanup.
   *
   *  ⚠ Heads-up for devs: the store stays dormant if no component
   *  ever mounts that calls `start()`. In particular, if
   *  `CHAT_API_URL` is unset (so `RecentChatsSection` early-returns
   *  `null`) the store will look empty even after login — that's
   *  expected, not a bug. */
  start = (): (() => void) => {
    if (this.started) return this.stop;
    this.started = true;

    // Refresh on login; clear on logout.
    this.disposeReaction = reaction(
      () => userStore.token,
      (token) => {
        if (token) {
          this.refresh().catch(console.error);
        } else {
          runInAction(() => {
            (this.chats as IObservableArray<ChatListItem>).clear();
            this.initialized = false;
          });
        }
      },
      { fireImmediately: true },
    );

    // Refetch when the tab comes back into focus so cross-tab edits
    // (rename/star/delete done in the Agents app) catch up here.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    return this.stop;
  };

  /** Tear down what `start()` set up. Safe to call when not started. */
  stop = () => {
    if (!this.started) return;
    this.started = false;
    this.disposeReaction?.();
    this.disposeReaction = null;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  };

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && userStore.token) {
      this.refresh().catch(console.error);
    }
  };

  @computed
  get visibleChats(): ChatListItem[] {
    return this.chats.filter((c) => !!c.title);
  }

  @computed
  get starred(): ChatListItem[] {
    return this.visibleChats.filter((c) => c.starred);
  }

  @computed
  get recent(): ChatListItem[] {
    return this.visibleChats.filter((c) => !c.starred);
  }

  @action
  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  fetchChats = async () => {
    if (this.fetching) return;
    runInAction(() => {
      this.fetching = true;
    });
    try {
      const chats = await listChats({ query: this.searchQuery });
      runInAction(() => {
        (this.chats as IObservableArray<ChatListItem>).replace(chats);
        this.initialized = true;
      });
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      runInAction(() => {
        this.fetching = false;
      });
    }
  };

  refresh = async () => {
    await this.fetchChats();
  };

  /** Rethrows on failure so callers (modal UIs) can keep themselves
   *  open and show a retry. The list still refreshes on success. */
  deleteChat = async (id: string) => {
    await apiDeleteChat(id);
    // Give the server a moment to settle before re-listing.
    await new Promise((resolve) => setTimeout(resolve, 100));
    await this.refresh();
  };

  toggleStarred = async (id: string) => {
    const current = this.chats.find((c) => c._id === id);
    const next = !current?.starred;
    try {
      await apiToggleStarChat(id, next);
    } catch (error) {
      console.error('Error toggling star:', error);
    }
    await this.refresh();
  };

  /** Rethrows on failure — the API call runs first, so the local
   *  title update + refresh below only happen on success. The
   *  refresh is for canonical re-ordering (starred-first, newest-
   *  first), not rollback. */
  renameChat = async (id: string, name: string) => {
    await apiRenameChat(id, name);
    runInAction(() => {
      const chat = this.chats.find((c) => c._id === id);
      if (chat) chat.title = name;
    });
    await new Promise((resolve) => setTimeout(resolve, 100));
    await this.refresh();
  };

  cloneChat = async (id: string): Promise<string | null> => {
    try {
      const result = await apiCloneChat(id);
      await this.refresh();
      return result?.id ?? null;
    } catch (error) {
      console.error('Error cloning chat:', error);
      return null;
    }
  };
}

export const chatListStore = new ChatListStore();
